import { sha256Fingerprint } from './stable.js';
import type { MutagenId } from './domain.js';

export type HeavenlyStem = '甲' | '乙' | '丙' | '丁' | '戊' | '己' | '庚' | '辛' | '壬' | '癸';
export type AlgorithmId = 'default' | 'zhongzhou';
export type YearDivide = 'normal' | 'exact';
export type HoroscopeDivide = 'normal' | 'exact';
export type AgeDivide = 'normal' | 'birthday';
export type DayDivide = 'forward' | 'current';

export interface FourTransformationTableRow {
  lu: string;
  quan: string;
  ke: string;
  ji: string;
}

export type FourTransformationTable = Readonly<Record<HeavenlyStem, Readonly<FourTransformationTableRow>>>;

export interface CalculationRulesetConfig {
  algorithm: AlgorithmId;
  yearDivide: YearDivide;
  horoscopeDivide: HoroscopeDivide;
  ageDivide: AgeDivide;
  dayDivide: DayDivide;
  fixLeap: boolean;
  language: 'zh-TW';
  mutagensSourceId: string;
  brightnessSourceId: string;
}

export interface CalculationRuleset {
  id: string;
  version: string;
  engine: 'iztro';
  engineVersion: '2.6.0';
  status: 'candidate' | 'frozen';
  config: Readonly<CalculationRulesetConfig>;
  mutagens: FourTransformationTable;
}

/**
 * CALC-COMMON-BOOK uses the ten-stem Four Transformations documented by iztro
 * as its default and attributed there to 《紫微斗數全書》.
 *
 * This constant is a versioned project rule, not a claim that all schools use
 * the same table.
 */
export const COMMON_BOOK_MUTAGENS: FourTransformationTable = Object.freeze({
  甲: Object.freeze({ lu: '廉貞', quan: '破軍', ke: '武曲', ji: '太陽' }),
  乙: Object.freeze({ lu: '天機', quan: '天梁', ke: '紫微', ji: '太陰' }),
  丙: Object.freeze({ lu: '天同', quan: '天機', ke: '文昌', ji: '廉貞' }),
  丁: Object.freeze({ lu: '太陰', quan: '天同', ke: '天機', ji: '巨門' }),
  戊: Object.freeze({ lu: '貪狼', quan: '太陰', ke: '右弼', ji: '天機' }),
  己: Object.freeze({ lu: '武曲', quan: '貪狼', ke: '天梁', ji: '文曲' }),
  庚: Object.freeze({ lu: '太陽', quan: '武曲', ke: '太陰', ji: '天同' }),
  辛: Object.freeze({ lu: '巨門', quan: '太陽', ke: '文曲', ji: '文昌' }),
  壬: Object.freeze({ lu: '天梁', quan: '紫微', ke: '左輔', ji: '武曲' }),
  癸: Object.freeze({ lu: '破軍', quan: '巨門', ke: '太陰', ji: '貪狼' }),
});

export const CALC_COMMON_BOOK_1_0_0_CANDIDATE: CalculationRuleset = Object.freeze({
  id: 'CALC-COMMON-BOOK',
  version: '1.0.0-candidate',
  engine: 'iztro',
  engineVersion: '2.6.0',
  status: 'candidate',
  config: Object.freeze({
    algorithm: 'default',
    yearDivide: 'normal',
    horoscopeDivide: 'normal',
    ageDivide: 'normal',
    dayDivide: 'forward',
    fixLeap: true,
    language: 'zh-TW',
    mutagensSourceId: 'SRC-IZTRO-DOC-003',
    brightnessSourceId: 'SRC-IZTRO-DOC-004',
  }),
  mutagens: COMMON_BOOK_MUTAGENS,
});

export async function calculationRulesetFingerprint(ruleset: CalculationRuleset): Promise<string> {
  return sha256Fingerprint({
    id: ruleset.id,
    version: ruleset.version,
    engine: ruleset.engine,
    engineVersion: ruleset.engineVersion,
    config: ruleset.config,
    mutagens: ruleset.mutagens,
  });
}

export function fourTransformationStar(
  stem: HeavenlyStem,
  mutagen: MutagenId,
  table: FourTransformationTable = COMMON_BOOK_MUTAGENS,
): string {
  return table[stem][mutagen];
}
